import ExperienceHighlights from './Experience';
import EducationLanguages from './Education';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DoubleStar from '../../assets/common/DoubleStar.svg';
import Image from '../../components/general/Image';
import DashedChips from '../../components/common/DashedChips';
import HireDeveloper from '../../assets/developer-details/HireBanner.png'
import Button from '../../components/general/Button';
import { EMPLOYMENT_TYPE } from '../../helpers/constants';

const DeveloperDetails = ({ onUnlockRatesClick }) => {
    let { developer: { detail } } = useSelector(state => state.common);

    const skills = useMemo(() => {
        return detail?.skills_experiences?.map(({ skill, experience }) => {
            return { name: `${skill} - ${experience} ${experience > 1 ? "years" : "year"}` }
        })
    }, [detail?.skills_experiences])

    const additionalSkills = useMemo(() => {
        if (!detail?.additional_skills) return [];
        return detail?.additional_skills?.split(",").map(skill => ({ name: skill }))
    }, [detail?.additional_skills])

    return (
        <div className="bg-black text-white px-[20px] lg:px-[40px] lg:pr-[100px] py-[50px] lg:py-[20px] lg:pb-[100px]">
            <h3 className="text-[30px] lg:text-[40px] font-bold flex justify-start">
                <span>{detail.first_name} – {detail?.specialization?.name}</span>
                <Image src={DoubleStar} alt="double-star" className="w-[40px] h-[40px] hidden lg:block ml-[10px]" />
            </h3>
            <p className='font-extralight text-[16px] my-[20px]'>{detail.about}</p>
            {Boolean(skills?.length) && <TechBlock title="Main technologies" skills={skills} containerClass="grid grid-cols-2 lg:flex lg:flex-wrap md:grid-cols-4 gap-[10px]" />}
            {Boolean(detail?.skills?.length) && <TechBlock title="Additional skills" skills={additionalSkills} containerClass="grid grid-cols-2 lg:flex lg:flex-wrap md:grid-cols-4 gap-[10px]" />}
            <StartOptions availability={detail?.availability} />

            <div className="relative bg-gradient-to-r from-orange-50 via-orange-100 to-orange-200 flex items-center justify-center px-2 py-3 md:py-2 rounded-[8px]">
                {/* Main content */}
                <div className="relative text-center max-w-2xl mx-auto">
                    <h1 className="text-xl md:text-[0.95rem] lg:text-[1.2rem] font-bold text-gray-800 mb-3 text-center">
                        Ready to get matched with vetted developers fast?
                    </h1>

                    <p className="text-orange-500 font-medium mb-3 text-center text-sm">
                        Let's get started today!
                    </p>

                    <Button
                        onClick={onUnlockRatesClick}
                        className="bg-orange-500 text-[0.95rem] text-black px-4 py-2 rounded-md duration-200 shadow-md transform hover:scale-105 transition-transform mx-auto"
                    >
                        Hire {detail.first_name} now <i className='fa-solid fa-arrow-right'></i>
                    </Button>
                </div>
            </div>

            <div className='my-[20px] lg:mt-[45px]'>
                <ExperienceHighlights />

                <div className="flex items-center gap-[10px] mt-[20px] bg-[#0090FF] rounded h-[40px] px-[10px] md:w-[80%] lg:w-[600px]">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
                    </svg>
                    <p className="text-white text-[12px] text-center"> Keep in mind, the experience summary might exclude non-relevant projects</p>
                </div>
            </div>
            <EducationLanguages />
        </div>
    );
};

const TechBlock = ({ title, skills, containerClass }) => (
    <div className="my-[20px] lg:my-[40px]">
        <h4 className='text-primary-color mb-[10px]'>{title}</h4>

        <div className={`grid ${containerClass}`}>
            {
                skills?.length && skills.map((skill, index) => (
                    <DashedChips keyName={index} text={skill.name} />
                ))
            }
        </div>
    </div>
);

const StartOptions = ({ availability }) => (
    <div className="flex gap-[10px] my-[20px]">
        <div className='bg-[#292929] rounded-[6px] p-[10px]'>
            <p className='text-[0.9rem]'>Ready to start</p>
            <p className='uppercase font-extralight text-[0.75rem]'>To be verified</p>
        </div>
        <div className='bg-[#292929] rounded-[6px] p-[10px]'>
            <p className='text-[0.9rem]'>Direct hire</p>
            <p className='uppercase font-extralight text-[0.75rem]'>Potentially possible</p>
        </div>
        <div className='bg-[#292929] rounded-[6px] p-[10px]'>
            <p className='text-[0.9rem]'>Availability</p>
            <p className='uppercase font-extralight text-[0.75rem]'>{EMPLOYMENT_TYPE[availability]?.displayValue}</p>
        </div>
    </div>
);

export default DeveloperDetails;